import Notification from "@/components/notification";
import useProfile from "@/hooks/use-profile";
import HamburgerButton from "@/layouts/hamburger-button";
import StickyHeader from "@/layouts/sticky-header";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiAlertTriangle, FiRefreshCw } from "react-icons/fi";
import { CarbonDrawerSidebar } from "./carbon-drawer-sidebar";
import cn from "@/utils/class-names";
import { Link } from "react-router-dom";
import { routes } from "@/config/routes";
import { IUserProfileStore, Roles } from "@/utilities/types/Users";
import { useNotification } from "@/components/notification/NotificationContext";
import { isLandingPagesEnabled } from "@/utilities/functions/getLandingPagesEnabled";
import LogoHeader from "@/components/logo-header";
import { request } from "@/utilities/libs/request";
import { Button, Popover, Title, Text } from "rizzui";
import { Download } from "lucide-react";
import EnableNotificationsPopup from "@/components/notification/enable-notifications-popup";
import { usePushNotification } from "@/hooks/usePushNotification";

enum E_NOTIFICATION_TYPE {
  PLAN_EXPIRED = "PLAN_EXPIRED",
  HAVE_PENDING_PAYMENT = "HAVE_PENDING_PAYMENT",
  PAYMENT_WAIT_APPROVAL = "PAYMENT_WAIT_APPROVAL",
}

const headerState = {
  [E_NOTIFICATION_TYPE.PLAN_EXPIRED]: {
    title: "Pricing Plan Expired",
    message: "Your plan has expired. Please renew your plan.",
    link: routes.package.plans,
    buttonText: "Renew",
    type: "error",
  },

  [E_NOTIFICATION_TYPE.HAVE_PENDING_PAYMENT]: {
    title: "Pending Payment",
    message: "You have a pending payment. Please continue to pay your payment",
    link: routes.package.plans,
    buttonText: "Pay",
    type: "warning",
  },
  [E_NOTIFICATION_TYPE.PAYMENT_WAIT_APPROVAL]: {
    title: "Pending Payment",
    message: "We are currently verifying your payment.",
    link: "/",
    buttonText: "",
    type: "warning",
  },
};

const HeaderToastBanner = ({ planType }: { planType: E_NOTIFICATION_TYPE }) => {
  const state = headerState[planType];

  return (
    <div
      className={cn(
        "bg-transparent rounded-lg shadow-lg p-4 flex gap-2 items-center",
        state?.buttonText ? "justify-between" : "justify-center",
        state?.type === "error" && "bg-red-500",
        state?.type === "warning" && "bg-yellow-500",
      )}
    >
      <span className="font-bold">
        <FiAlertTriangle className="lg:w-6 lg:h-6 w-4 h-4 text-white" />
      </span>
      <p className="text-white font-bold lg:text-base text-xs gap-2">
        {state?.message}
      </p>
      {!!state?.buttonText && (
        <Link
          to={state?.link}
          className={cn(
            "bg-white lg:text-base text-xs lg:top-[60px] h-auto block whitespace-nowrap font-bold text-red-500 lg:ml-20 hover:bg-white/80 px-4 py-2 rounded-lg",
            state?.type === "error" && "text-red-500",
            state?.type === "warning" && "text-yellow-500",
          )}
        >
          {state?.buttonText}
        </Link>
      )}
    </div>
  );
};

const getToastBanner = (
  profile?: IUserProfileStore | null,
): E_NOTIFICATION_TYPE | null => {
  if (!profile || profile.role === Roles.ADMIN || profile.role === Roles.USER)
    return null;

  if (profile.isVerifying) {
    return E_NOTIFICATION_TYPE.PAYMENT_WAIT_APPROVAL;
  }

  if (!isLandingPagesEnabled(profile.userFeatures || [])) {
    return null;
  }

  if (profile.isExistPendingPayment) {
    return E_NOTIFICATION_TYPE.HAVE_PENDING_PAYMENT;
  }

  if (profile.statusAccount === "inactive" || profile.isExpiredPackage) {
    return E_NOTIFICATION_TYPE.PLAN_EXPIRED;
  }

  return null;
};

const clearAllCache = () => {
  return request<void>({
    url: "/script/cache/clear-all",
    method: "DELETE",
  });
};

const INSTALL_TOOLTIP_SHOWN_KEY = "install_tooltip_shown";
const PUSH_PROMPT_DISMISSED_KEY = "push_prompt_dismissed";

export default function Header() {
  const { addNotification } = useNotification();

  const { profile } = useProfile();
  const [toastBanner, setToastBanner] = useState<E_NOTIFICATION_TYPE | null>(
    null,
  );
  const [isClearingCache, setIsClearingCache] = useState(false);
  const [showInstallTooltip, setShowInstallTooltip] = useState(false);
  const [isInstallPopoverOpen, setIsInstallPopoverOpen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showEnablePopup, setShowEnablePopup] = useState(false);
  const { isSupported, permission, isSubscribed } = usePushNotification();

  useEffect(() => {
    const typedNavigator = navigator as Navigator & { standalone?: boolean };
    const isStandaloneMode =
      window.matchMedia("(display-mode: standalone)").matches ||
      typedNavigator.standalone === true;
    setIsStandalone(isStandaloneMode);
  }, []);

  useEffect(() => {
    const hasShownTooltip = localStorage.getItem(INSTALL_TOOLTIP_SHOWN_KEY);
    if (!hasShownTooltip && profile?.role !== Roles.ADMIN) {
      setShowInstallTooltip(true);
      setIsInstallPopoverOpen(true);
      localStorage.setItem(INSTALL_TOOLTIP_SHOWN_KEY, "true");
    }
  }, [profile]);

  useEffect(() => {
    if (!isStandalone) return;
    if (!isSupported) return;
    if (isSubscribed) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(PUSH_PROMPT_DISMISSED_KEY) == "true") return;

    const timer = setTimeout(() => setShowEnablePopup(true), 500);
    return () => clearTimeout(timer);
  }, [isStandalone, isSupported, permission, isSubscribed]);

  const handleClearCache = async () => {
    setIsClearingCache(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, 1500));
    try {
      await Promise.all([clearAllCache(), minDelay]);
      toast.success("Cache cleared successfully");
    } catch (error) {
      toast.error("Failed to clear cache");
    } finally {
      setIsClearingCache(false);
    }
  };

  const handlePermissionResult = async (result: NotificationPermission) => {
    if (result === "granted") {
      toast.success("Notifications enabled successfully!");
    } else if (result === "denied") {
      toast("You can enable notifications in browser settings later.");
    }
    localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, "true");
  };

  const handlePopupClose = () => {
    setShowEnablePopup(false);
  };

  useEffect(() => {
    if (!toastBanner || !profile) return;

    if (profile?.statusAccount === "inactive") {
      addNotification({
        title: "Pricing Plan Expired",
        message: (
          <p>
            Your plan has expired. Please contact ProjectSG to renew your
            package. Click{" "}
            <a
              className="font-bold underline"
              target="_blank"
              href="https://wa.me/projectsg"
            >
              here
            </a>{" "}
            to contact us.
          </p>
        ),
        type: "info",
        timestamp: new Date("2025-04-14").toISOString(),
        id: E_NOTIFICATION_TYPE.PLAN_EXPIRED,
      });
    }

    const toastId = "toast-banner";

    if (toastBanner) {
      toast.custom(
        (t) => (
          <div
            className={cn(
              "lg:block hidden",
              t.visible
                ? "animate-in fade-in-0 zoom-in-95"
                : "animate-out fade-out-0 zoom-out-95",
            )}
          >
            <HeaderToastBanner planType={toastBanner} />
          </div>
        ),
        {
          id: toastId,
          duration: Infinity,
          position: "top-center",
        },
      );
    }
    return () => {
      toast.remove(toastId);
    };
  }, [toastBanner, profile, addNotification]);

  useEffect(() => {
    if (!profile) return;
    setToastBanner(getToastBanner(profile));
  }, [profile]);

  return (
    <StickyHeader className="z-[990] w-full p-4 ">
      <div className="w-full space-y-2">
        <div className="flex items-center justify-between flex-1 w-full">
          <HamburgerButton
            view={
              <CarbonDrawerSidebar className="static h-full w-full 2xl:w-full" />
            }
          />
          <div className="flex items-center flex-1 justify-center">
            <Link
              to={"/"}
              aria-label="Site Logo"
              className="xl:hidden me-4 shrink-0 text-gray-800 lg:me-5"
            >
              <LogoHeader className="max-w-[200px]" />
            </Link>

            {/* <SearchWidget /> */}
            {/* <LanguageSwitcher /> */}
          </div>

          <div className="flex items-center gap-2">
            {profile?.role === Roles.ADMIN && (
              <Popover placement="bottom-end">
                <Popover.Trigger>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={isClearingCache}
                  >
                    <FiRefreshCw
                      className={cn(
                        "w-4 h-4 me-1",
                        isClearingCache &&
                          "animate-spin [animation-duration:3s]",
                      )}
                    />
                  </Button>
                </Popover.Trigger>
                <Popover.Content className="z-[9999]">
                  {({ setOpen }) => (
                    <div className="w-56 pb-2 pt-1 text-left rtl:text-right">
                      <Title
                        as="h6"
                        className="mb-0.5 flex items-start text-sm text-gray-700 sm:items-center"
                      >
                        Clear Cache
                      </Title>
                      <Text className="mb-2 leading-relaxed text-gray-500 text-sm">
                        Are you sure you want to clear all cache?
                      </Text>
                      <div className="flex items-center justify-end">
                        <Button
                          size="sm"
                          className="me-1.5 h-7"
                          onClick={() => {
                            handleClearCache();
                            setOpen(false);
                          }}
                        >
                          Yes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7"
                          onClick={() => setOpen(false)}
                        >
                          No
                        </Button>
                      </div>
                    </div>
                  )}
                </Popover.Content>
              </Popover>
            )}
            {profile?.role !== Roles.ADMIN && !isStandalone && (
              <Popover
                placement="bottom-start"
                isOpen={isInstallPopoverOpen}
                setIsOpen={setIsInstallPopoverOpen}
              >
                <Popover.Trigger>
                  <Link to={routes.install} className="px-4">
                    <Download size={22} className="cursor-pointer" />
                  </Link>
                </Popover.Trigger>
                <Popover.Content
                  className={cn(
                    "z-[999999999]",
                    showInstallTooltip &&
                      "animate-in fade-in zoom-in-95 duration-200",
                  )}
                >
                  <Text className="font-medium text-sm">
                    Install app to your device
                  </Text>
                </Popover.Content>
              </Popover>
            )}
            <Notification />
          </div>
        </div>

        {toastBanner && (
          <div className="lg:hidden">
            <HeaderToastBanner planType={toastBanner} />
          </div>
        )}
      </div>

      {/* <HeaderMenuRight /> */}
      <EnableNotificationsPopup
        isOpen={showEnablePopup}
        onClose={handlePopupClose}
        onPermissionResult={handlePermissionResult}
      />
    </StickyHeader>
  );
}
